import { keepPreviousData } from '@tanstack/react-query'
import { uniqBy } from 'lodash'
import { useEffect, useState } from 'react'

import { ItchConnectionDtoCursorPaginatedResponse, useGetAllClientMarketItchConnections } from '@/gen/publicApi'

export function useGetAllClientMarketItchConnectionsInfinite(
    clientCode: string,
    marketCode: string,
    includeDeleted: boolean,
) {
    const [cursor, setCursor] = useState<string | undefined>(undefined)
    const [pages, setPages] = useState<ItchConnectionDtoCursorPaginatedResponse['data']>([])
    const [nextCursor, setNextCursor] = useState<string | undefined>(undefined)
    const { data, isLoading } = useGetAllClientMarketItchConnections(
        clientCode,
        marketCode,
        {
            Cursor: cursor,
            includeDeleted,
        },
        {
            query: {
                placeholderData: keepPreviousData,
                suspense: true,
            },
        },
    )

    // Reset pages and cursor when clientCode, marketCode, or includeDeleted changes
    useEffect(() => {
        setPages([])
        setCursor(undefined)
    }, [clientCode, marketCode, includeDeleted])

    useEffect(() => {
        if (!data) {
            return
        }

        setNextCursor(data?.nextCursor || undefined)

        // If the cursor has changed (fetching next page), concatenate the new data
        if (cursor) {
            const newData = pages?.concat(data.data || [])
            const newPages = uniqBy(newData, page => page.id)
            setPages(newPages)
        } else {
            // If cursor is undefined (first fetch or reset), replace the data entirely
            setPages(data.data || [])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, cursor])

    const fetchNextPage = () => {
        if (!nextCursor) {
            return
        }

        setCursor(nextCursor)
    }

    return {
        data: pages,
        isLoading,
        fetchNextPage,
        hasNextPage: nextCursor !== undefined,
    }
}
